import { useAppDispatch } from "../../app/store";
import { WideWidthContainer } from "../../common/components/layout/Container";
import Heading from "../../common/components/ui/Heading";
import { ButtonMedium } from "../../common/components/ui/buttons";
import { locationSelectors } from "../../common/store/slice/locationSlice";
import { questionnaireActions } from "../../common/store/slice/questionnaireSlice";
import { userPlanActions } from "../../common/store/slice/userPlanSlice";
import Description from "./Description";
import QuestionnaireCategoryTag from "./QuestionnaireCategoryTag";
import { MultipleChoice } from "./answers/MultipleChoice";
import { Searchable } from "./answers/Searchable";
import { Slider } from "./answers/Slider";
import { generateChoiceTextFromOptional } from "./helpers/generateChoiceTextFromOptional";
import { RegionalNamespaces } from "@pslifestyle/common/src/constants/translations";
import { QuestionType } from "@pslifestyle/common/src/schemas";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  question: QuestionType;
}

const Question: React.FC<Props> = ({ question }) => {
  const country = useSelector(locationSelectors.country);
  const dispatch = useAppDispatch();
  const answerQuestion = useCallback(
    (choiceText: string | null) => {
      dispatch(userPlanActions.resetUserPlan());
      dispatch(
        questionnaireActions.addAnswer({
          questionId: question.id,
          sortKey: question.sortKey,
          choiceText,
        }),
      );
    },
    [dispatch, question.id, question.sortKey],
  );
  const { t } = useTranslation([
    "common",
    "questionnaire",
    RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
  ]);

  if (!question || !question.choices) return null;
  const renderInputTypes = (question: QuestionType) => {
    if (question.variableName === "DEMOGRAPHIC_AGE") {
      return (
        <Slider
          min={0}
          max={100}
          handleSubmit={(value) =>
            answerQuestion(generateChoiceTextFromOptional(value))
          }
        />
      );
    }

    if (question.variableName === "DEMOGRAPHIC_LOCATION") {
      return <Searchable question={question} handleSubmit={answerQuestion} />;
    }
    return (
      <MultipleChoice question={question} handleChoiceClick={answerQuestion} />
    );
  };
  return (
    <WideWidthContainer className="px-4">
      <QuestionnaireCategoryTag sortKey={question.sortKey} />
      <div
        key={question.id}
        data-cy={`question.${question.id}`}
        className="flex flex-col justify-center items-center child:pb-6"
      >
        <Heading level={1} type="headline-sm-b" className="text-center">
          {t(`${question.id}_${country?.code}`, {
            ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
          })}
        </Heading>

        <Description
          descriptionTranslationKey={question.descriptionTranslationKey}
          country={country!}
        />
        {renderInputTypes(question)}
        {question.label === "demographic" && (
          <ButtonMedium
            theme="tertiary"
            className="text-grey-60"
            onClick={() => answerQuestion(null)}
          >
            {t("buttonSkipThisQuestion", { ns: "questionnaire" })}
          </ButtonMedium>
        )}
      </div>
    </WideWidthContainer>
  );
};

export default Question;
